import { camelCase, startCase, toLower } from 'lodash-es'
import type { TFunction } from 'react-i18next'
import type { ComboboxItem, HighlightType } from '@node-space/ui-components'
import { CustomerUseCase, type CustomerCustodyModel, type CustomerV2Status } from 'types/customers'
import { DOCUMENT_TYPES, REQUIRED_ACTION_CODE_DOCUMENT_REQUIRED } from '../helpers/constants'

export const formatEnumValue = (value: string) => startCase(toLower(value))

export const statusHighlightTypeMap: Partial<Record<CustomerV2Status, HighlightType>> = {
  VERIFIED: 'success',
  INFO_REQUIRED: 'warning',
  ACTIONS_REQUIRED: 'warning',
  PENDING: 'neutral',
  REJECTED: 'error',
  TERMINATED: 'error',
  AGREEMENTS_PENDING: 'warning',
}

export const customerTypeHighlightMap: Partial<Record<string, HighlightType>> = {
  INDIVIDUAL: 'feature',
}

export const customerTypeLabelMap: Record<string, string> = {
  COMPANY: 'Business',
  INDIVIDUAL: 'Individual',
}

export const custodyModelLabelMap: Record<CustomerCustodyModel, string> = {
  SELF_CUSTODY: 'User self-hosted',
  CUSTODIAL: 'Custodial',
}

export const custodyModelUseCasesMap: Record<CustomerCustodyModel, CustomerUseCase[]> = {
  SELF_CUSTODY: [CustomerUseCase.SELF_CUSTODY],
  CUSTODIAL: [
    CustomerUseCase.FIAT,
    CustomerUseCase.CRYPTO,
    CustomerUseCase.STABLECOIN_PAYOUTS,
    CustomerUseCase.EMBEDDED_STABLECOIN_WALLETS,
    CustomerUseCase.EMBEDDED_FIAT_ACCOUNTS,
  ],
}

export const statusLabelMap: Partial<Record<CustomerV2Status, string>> = {
  VERIFIED: 'Verified',
  INFO_REQUIRED: 'Info Required',
  PENDING: 'Pending',
  ACTIONS_REQUIRED: 'Actions Required',
  REJECTED: 'Rejected',
  TERMINATED: 'Terminated',
  AGREEMENTS_PENDING: 'Agreements Pending',
}

export const inFlightStatusLabelMap: Partial<Record<CustomerV2Status, string>> = {
  INFO_REQUIRED: 'Info Required',
  PENDING: 'Pending',
  ACTIONS_REQUIRED: 'Actions Required',
  REJECTED: 'Rejected',
  TERMINATED: 'Terminated',
  AGREEMENTS_PENDING: 'Agreements Pending',
}

export const CUSTOMERS_V2_DEFAULT_STATUSES = Object.keys(
  inFlightStatusLabelMap
) as CustomerV2Status[]

export const getOptions = (
  map: Record<string, string>,
  translationPrefix: string,
  t: (key: string) => string
) =>
  Object.keys(map).map(key => ({
    value: map[key],
    leftText: t(`${translationPrefix}.${key}`),
  }))

export const findLabel = (items: ComboboxItem[], value: string | undefined): string | undefined =>
  items.find(item => item.value === value)?.leftText as string | undefined

export const booleanDisplay = (value: string | undefined, yes: string, no: string): string => {
  if (value === 'true') return yes
  if (value === 'false') return no
  return ''
}

export const getPendingDocumentCode = (action: { code: string; description?: string }) =>
  action.code === REQUIRED_ACTION_CODE_DOCUMENT_REQUIRED
    ? camelCase(action.description)
    : camelCase(action.code)

export const getHasOutstandingDocumentActions = (pendingCodes: Set<string>) =>
  DOCUMENT_TYPES.some(docKey => pendingCodes.has(docKey))

export const getVerificationHelpTextKey = (
  showDocumentsPending: boolean,
  showAssociatesPending: boolean,
  isInfoRequired: boolean
): string => {
  if (showDocumentsPending) return 'embeddedCustomers.details.verificationCtaDocumentsPending'
  if (showAssociatesPending) return 'embeddedCustomers.details.verificationCtaAssociatesPending'
  if (isInfoRequired) return 'embeddedCustomers.details.verificationCtaPending'
  return 'embeddedCustomers.details.verificationCtaComplete'
}

export const getIndividualCTAHelpTextKey = (
  showIdvLink: boolean,
  hasDocumentRequirements: boolean
): string => {
  if (showIdvLink) return 'customers.idvLinkHelp'
  if (hasDocumentRequirements) return 'customers.verificationDocumentsPendingCta'
  return 'customers.onboardingLinkHelp'
}

export const getV2OnboardingHelpText = (
  t: TFunction,
  hasOutstandingDocuments: boolean,
  hasCompanyInfoRequired: boolean,
  requiresAssociateData: boolean
): string => {
  const items = [
    hasOutstandingDocuments && t('customers.pendingItem.documents'),
    hasCompanyInfoRequired && t('customers.pendingItem.companyDetails'),
    requiresAssociateData && t('customers.pendingItem.associates'),
  ].filter(Boolean)

  return items.length > 0
    ? t('customers.verificationCtaItemsPending', { items: items.join(', ') })
    : t('customers.onboardingLinkHelp')
}
